import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import PrimaryNavigation from 'components/ui/PrimaryNavigation';
import UserContextMenu from 'components/ui/UserContextMenu';
import QuickActionPanel from 'components/ui/QuickActionPanel';

import StepIndicator from './components/StepIndicator';
import IncomeStep from './components/IncomeStep';
import ExpensesStep from './components/ExpensesStep';
import GoalsStep from './components/GoalsStep';
import PreferencesStep from './components/PreferencesStep';
import BudgetPreview from './components/BudgetPreview';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const TOTAL_STEPS = 4;

const STEP_META = [
  { title: 'Income & Employment', subtitle: 'Tell us about your earnings', icon: 'DollarSign' },
  { title: 'Monthly Expenses', subtitle: 'Where does your money go?', icon: 'CreditCard' },
  { title: 'Financial Goals', subtitle: 'What are you saving for?', icon: 'Target' },
  { title: 'Preferences & Plan', subtitle: 'Customize your experience', icon: 'Sliders' },
];

const INCOME_RANGE_MAP = {
  'under-25k': 2000,
  '25k-50k': 3100,
  '50k-75k': 5200,
  '75k-100k': 7300,
  '100k-150k': 10400,
  'over-150k': 15000,
};

const initialFormData = {
  income: { incomeRange: '', employmentType: '', additionalIncome: '' },
  expenses: {},
  goals: { selectedGoals: [], goalDetails: {} },
  preferences: {
    riskTolerance: 'moderate',
    notifications: { budgetAlerts: true, weeklyReport: true, goalMilestones: true, arScanTips: false, financialTips: true },
    plan: 'free',
  },
};

function validateStep(step, formData) {
  switch (step) {
    case 1: return !!formData?.income?.incomeRange && !!formData?.income?.employmentType;
    case 2: return Object.values(formData?.expenses)?.some((v) => parseFloat(v) > 0);
    case 3: return (formData?.goals?.selectedGoals || [])?.length > 0;
    case 4: return !!formData?.preferences?.riskTolerance && !!formData?.preferences?.plan;
    default: return true;
  }
}

// ─── Auth Panel ───────────────────────────────────────────────────────────────
function AuthPanel({ onSuccess }) {
  const { signIn, signUp, resetPassword, updatePassword } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'forgot' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Detect password reset token in URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location?.search);
    const hash = window.location?.hash;
    if (params?.get('reset') === 'true' || hash?.includes('type=recovery')) {
      setMode('reset');
    }
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);
    try {
      if (mode === 'forgot') {
        let result = await resetPassword(email);
        if (result?.error) {
          setError(result?.error?.message);
        } else {
          setSuccessMsg('Password reset email sent! Check your inbox and follow the link to reset your password.');
        }
        return;
      }

      if (mode === 'reset') {
        if (password !== confirmPassword) {
          setError('Passwords do not match.');
          return;
        }
        if (password?.length < 6) {
          setError('Password must be at least 6 characters.');
          return;
        }
        let result = await updatePassword(password);
        if (result?.error) {
          setError(result?.error?.message);
        } else {
          setSuccessMsg('Password updated successfully! You can now sign in.');
          setTimeout(() => {
            setMode('login');
            setSuccessMsg('');
            setPassword('');
            setConfirmPassword('');
            // Clear reset params from URL
            window.history?.replaceState({}, '', window.location?.pathname);
          }, 2000);
        }
        return;
      }

      let result;
      if (mode === 'login') {
        result = await signIn(email, password);
      } else {
        result = await signUp(email, password, fullName);
      }
      if (result?.error) {
        setError(result?.error?.message);
      } else {
        if (mode === 'signup') {
          setSuccessMsg('Account created! Signing you in...');
          setTimeout(() => onSuccess(), 800);
        } else {
          onSuccess();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccessMsg('');
  };

  const isForgotOrReset = mode === 'forgot' || mode === 'reset';

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#06B6D4] to-[#0891B2] flex items-center justify-center shadow-lg">
            <Icon name="Scan" size={22} color="#0F172A" strokeWidth={2.5} />
          </div>
          <span className="font-heading font-bold text-2xl text-[#F8FAFC] tracking-tight">
            FinVis<span className="text-[#06B6D4]">AR</span>
          </span>
        </div>

        <div className="rounded-2xl border border-[rgba(6,182,212,0.15)] bg-[rgba(15,23,42,0.9)] backdrop-blur-sm p-6 md:p-8">
          <h2 className="text-xl font-bold text-[#F8FAFC] mb-1">
            {mode === 'login' && 'Welcome back'}
            {mode === 'signup' && 'Create your account'}
            {mode === 'forgot' && 'Reset your password'}
            {mode === 'reset' && 'Set new password'}
          </h2>
          <p className="text-sm text-[#94A3B8] mb-6">
            {mode === 'login' && 'Sign in to your FinVisAR account'}
            {mode === 'signup' && 'Start your financial journey today'}
            {mode === 'forgot' && "Enter your email and we'll send you a reset link"}
            {mode === 'reset' && 'Choose a strong new password for your account'}
          </p>

          {/* Demo credentials hint */}
          {mode === 'login' && (
            <div className="mb-4 p-3 rounded-lg bg-[rgba(6,182,212,0.06)] border border-[rgba(6,182,212,0.15)]">
              <p className="text-xs text-[#94A3B8]">
                <span className="text-[#06B6D4] font-medium">Demo:</span> demo@finvisar.com / demo1234
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e?.target?.value)}
                  placeholder="Alex Morgan"
                  className="w-full px-4 py-3 rounded-xl bg-[#0A1628] border border-[rgba(6,182,212,0.15)] text-[#F8FAFC] text-sm placeholder-[#475569] focus:outline-none focus:border-[#06B6D4] transition-colors min-h-[44px]"
                />
              </div>
            )}

            {mode !== 'reset' && (
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-[#0A1628] border border-[rgba(6,182,212,0.15)] text-[#F8FAFC] text-sm placeholder-[#475569] focus:outline-none focus:border-[#06B6D4] transition-colors min-h-[44px]"
                />
              </div>
            )}

            {(mode === 'login' || mode === 'signup' || mode === 'reset') && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-[#94A3B8]">
                    {mode === 'reset' ? 'New Password' : 'Password'}
                  </label>
                  {mode === 'login' && (
                    <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="text-xs text-[#06B6D4] hover:text-[#22D3EE] transition-colors"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="w-full px-4 py-3 pr-11 rounded-xl bg-[#0A1628] border border-[rgba(6,182,212,0.15)] text-[#F8FAFC] text-sm placeholder-[#475569] focus:outline-none focus:border-[#06B6D4] transition-colors min-h-[44px]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#94A3B8] transition-colors"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} color="currentColor" />
                  </button>
                </div>
              </div>
            )}

            {mode === 'reset' && (
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-1.5">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e?.target?.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="w-full px-4 py-3 pr-11 rounded-xl bg-[#0A1628] border border-[rgba(6,182,212,0.15)] text-[#F8FAFC] text-sm placeholder-[#475569] focus:outline-none focus:border-[#06B6D4] transition-colors min-h-[44px]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#94A3B8] transition-colors"
                  >
                    <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={16} color="currentColor" />
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.25)]">
                <Icon name="AlertCircle" size={14} color="#EF4444" />
                <p className="text-xs text-[#EF4444]">{error}</p>
              </div>
            )}

            {successMsg && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[rgba(16,185,129,0.08)] border border-[rgba(16,185,129,0.25)]">
                <Icon name="CheckCircle" size={14} color="#10B981" />
                <p className="text-xs text-[#10B981]">{successMsg}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-[#06B6D4] text-[#0F172A] font-semibold text-sm hover:bg-[#22D3EE] transition-colors min-h-[44px] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon
                  name={mode === 'login' ? 'LogIn' : mode === 'signup' ? 'UserPlus' : mode === 'forgot' ? 'Mail' : 'Lock'}
                  size={16}
                  color="#0F172A"
                />
              )}
              {mode === 'login' && 'Sign In'}
              {mode === 'signup' && 'Create Account'}
              {mode === 'forgot' && 'Send Reset Link'}
              {mode === 'reset' && 'Update Password'}
            </button>
          </form>

          {mode === 'forgot' ? (
            <p className="text-center text-xs text-[#64748B] mt-4">
              Remember your password?{' '}
              <button
                onClick={() => switchMode('login')}
                className="text-[#06B6D4] hover:text-[#22D3EE] font-medium transition-colors"
              >
                Back to sign in
              </button>
            </p>
          ) : mode === 'reset' ? (
            <p className="text-center text-xs text-[#64748B] mt-4">
              <button
                onClick={() => switchMode('login')}
                className="text-[#06B6D4] hover:text-[#22D3EE] font-medium transition-colors"
              >
                Back to sign in
              </button>
            </p>
          ) : (
            <p className="text-center text-xs text-[#64748B] mt-4">
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button
                onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')}
                className="text-[#06B6D4] hover:text-[#22D3EE] font-medium transition-colors"
              >
                {mode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          )}

          {/* Retail Partner & Admin links */}
          {mode === 'login' && (
            <>
              {/* Divider */}
              <div className="flex items-center gap-3 mt-5">
                <div className="flex-1 h-px bg-[rgba(148,163,184,0.15)]" />
                <span className="text-xs text-[#475569]">or</span>
                <div className="flex-1 h-px bg-[rgba(148,163,184,0.15)]" />
              </div>

              {/* Retail Partner Button */}
              <a
                href="/retail-partner-portal"
                className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-[rgba(6,182,212,0.4)] text-[#06B6D4] text-sm font-medium hover:bg-[rgba(6,182,212,0.08)] hover:border-[#06B6D4] transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                Are you a Retail Partner? Access Partner Portal
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </a>

              {/* Admin link */}
              <p className="text-center mt-3 mb-1">
                <a
                  href="/admin-management-dashboard"
                  className="text-xs text-[#475569] hover:text-[#64748B] transition-colors"
                >
                  Admin? Login here
                </a>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ─── Main Onboarding Component ────────────────────────────────────────────────
export default function OnboardingDashboard() {
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveError, setSaveError] = useState('');

  // If already authenticated and onboarding completed, redirect to dashboard
  React.useEffect(() => {
    if (isAuthenticated && userProfile?.onboarding_completed) {
      navigate('/home-dashboard');
    }
  }, [isAuthenticated, userProfile, navigate]);

  // Show auth panel if not authenticated
  if (!isAuthenticated) {
    return <AuthPanel onSuccess={() => {}} />;
  }

  const updateStepData = (step, data) => {
    const keys = ['income', 'expenses', 'goals', 'preferences'];
    setFormData((prev) => ({ ...prev, [keys?.[step - 1]]: data }));
  };

  const handleNext = () => {
    const isValid = validateStep(currentStep, formData);
    if (!isValid) {
      const msgs = [
        'Please select your income range and employment type.',
        'Please enter at least one expense amount.',
        'Please select at least one financial goal.',
        'Please select your risk tolerance and plan.',
      ];
      setValidationError(msgs?.[currentStep - 1]);
      return;
    }
    setValidationError('');
    setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
    if (currentStep < TOTAL_STEPS) setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    setValidationError('');
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleGenerate = async () => {
    const isValid = validateStep(4, formData);
    if (!isValid) {
      setValidationError('Please complete all preferences before generating your budget plan.');
      return;
    }
    setValidationError('');
    setSaveError('');
    setIsGenerating(true);

    try {
      const monthlyIncome = INCOME_RANGE_MAP?.[formData?.income?.incomeRange] || 3000;
      const totalBudget = Math.round(monthlyIncome * 0.8);

      // Save budget
      const { data: budgetData, error: budgetError } = await supabase?.from('budgets')?.insert({
          user_id: user?.id,
          monthly_income: monthlyIncome,
          total_budget: totalBudget,
          employment_type: formData?.income?.employmentType || 'full_time',
          risk_tolerance: formData?.preferences?.riskTolerance || 'moderate',
          plan: formData?.preferences?.plan || 'free',
          month_year: new Date()?.toISOString()?.slice(0, 7),
        })?.select()?.single();

      if (budgetError) throw budgetError;

      // Save spending categories from expenses
      const CATEGORY_META = {
        housing: { icon: 'Home', color: '#06B6D4', name: 'Housing' },
        groceries: { icon: 'ShoppingCart', color: '#10B981', name: 'Groceries' },
        dining: { icon: 'UtensilsCrossed', color: '#F59E0B', name: 'Dining Out' },
        transport: { icon: 'Car', color: '#7C3AED', name: 'Transportation' },
        entertainment: { icon: 'Tv', color: '#EC4899', name: 'Entertainment' },
        shopping: { icon: 'ShoppingBag', color: '#EF4444', name: 'Shopping' },
        healthcare: { icon: 'Heart', color: '#3B82F6', name: 'Healthcare' },
        utilities: { icon: 'Zap', color: '#F97316', name: 'Utilities' },
      };

      const categoryInserts = Object.entries(formData?.expenses || {})?.filter(([, v]) => parseFloat(v) > 0)?.map(([key, val]) => ({
          user_id: user?.id,
          budget_id: budgetData?.id,
          name: CATEGORY_META?.[key]?.name || key,
          icon: CATEGORY_META?.[key]?.icon || 'Tag',
          color: CATEGORY_META?.[key]?.color || '#06B6D4',
          monthly_limit: parseFloat(val),
          spent: 0,
        }));

      if (categoryInserts?.length > 0) {
        await supabase?.from('spending_categories')?.insert(categoryInserts);
      }

      // Save financial goals
      const GOAL_META = {
        emergency_fund: { icon: 'Shield', color: '#10B981', title: 'Emergency Fund' },
        vacation: { icon: 'Plane', color: '#06B6D4', title: 'Vacation Fund' },
        home: { icon: 'Home', color: '#7C3AED', title: 'Home Down Payment' },
        retirement: { icon: 'TrendingUp', color: '#F59E0B', title: 'Retirement Savings' },
        debt: { icon: 'CreditCard', color: '#EF4444', title: 'Debt Repayment' },
        education: { icon: 'BookOpen', color: '#3B82F6', title: 'Education Fund' },
      };

      const goalInserts = (formData?.goals?.selectedGoals || [])?.map((goalKey) => {
        const details = formData?.goals?.goalDetails?.[goalKey] || {};
        const meta = GOAL_META?.[goalKey] || { icon: 'Target', color: '#06B6D4', title: goalKey };
        return {
          user_id: user?.id,
          title: meta.title,
          icon: meta.icon,
          color: meta.color,
          target_amount: parseFloat(details?.targetAmount) || 5000,
          current_amount: parseFloat(details?.currentAmount) || 0,
          monthly_contribution: parseFloat(details?.monthlyContribution) || 100,
          deadline: details?.deadline || null,
          goal_status: 'active',
        };
      });

      if (goalInserts?.length > 0) {
        await supabase?.from('financial_goals')?.insert(goalInserts);
      }

      // Mark onboarding complete
      await supabase?.from('user_profiles')?.update({ onboarding_completed: true })?.eq('id', user?.id);

      setIsGenerating(false);
      setShowSuccess(true);
      await new Promise((r) => setTimeout(r, 1800));
      navigate('/home-dashboard');
    } catch (err) {
      setIsGenerating(false);
      setSaveError(err?.message || 'Failed to save your budget plan. Please try again.');
    }
  };

  const stepData = [formData?.income, formData?.expenses, formData?.goals, formData?.preferences];
  const currentMeta = STEP_META?.[currentStep - 1];

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <IncomeStep data={formData?.income} onChange={(d) => updateStepData(1, d)} />;
      case 2: return <ExpensesStep data={formData?.expenses} onChange={(d) => updateStepData(2, d)} />;
      case 3: return <GoalsStep data={formData?.goals} onChange={(d) => updateStepData(3, d)} />;
      case 4: return <PreferencesStep data={formData?.preferences} onChange={(d) => updateStepData(4, d)} />;
      default: return null;
    }
  };

  const displayUser = {
    name: userProfile?.full_name || user?.email?.split('@')?.[0] || 'User',
    email: user?.email || '',
    role: userProfile?.role || 'consumer',
    avatar: userProfile?.avatar_url || '',
  };

  return (
    <div className="min-h-screen bg-[#020617]">
      <PrimaryNavigation />
      <div className="hidden md:block fixed top-3 right-6 z-[110]">
        <UserContextMenu user={displayUser} />
      </div>
      <main className="pt-4 md:pt-6 pb-24 md:pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#06B6D4] to-[#0891B2] flex items-center justify-center shadow-lg">
                <Icon name="Settings2" size={20} color="#0F172A" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-[#F8FAFC]">Financial Setup</h1>
                <p className="text-sm text-[#94A3B8]">Step {currentStep} of {TOTAL_STEPS} — {currentMeta?.subtitle}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-5">
              <div className="p-4 md:p-5 rounded-xl border border-[rgba(6,182,212,0.15)] bg-[rgba(15,23,42,0.8)] backdrop-blur-sm">
                <StepIndicator currentStep={currentStep} completedSteps={completedSteps} />
              </div>

              <div className="rounded-xl border border-[rgba(6,182,212,0.15)] bg-[rgba(15,23,42,0.8)] backdrop-blur-sm overflow-hidden">
                <div className="flex items-center gap-3 px-4 md:px-6 py-4 border-b border-[rgba(6,182,212,0.1)] bg-[rgba(6,182,212,0.04)]">
                  <div className="w-8 h-8 rounded-lg bg-[rgba(6,182,212,0.15)] flex items-center justify-center">
                    <Icon name={currentMeta?.icon} size={16} color="#06B6D4" strokeWidth={2} />
                  </div>
                  <div>
                    <h2 className="text-base md:text-lg font-semibold text-[#F8FAFC]">{currentMeta?.title}</h2>
                    <p className="text-xs text-[#64748B]">{currentMeta?.subtitle}</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5">
                    <span className="text-xs text-[#64748B]">{currentStep}/{TOTAL_STEPS}</span>
                    <div className="w-16 h-1.5 rounded-full bg-[rgba(6,182,212,0.15)] overflow-hidden">
                      <div className="h-full bg-[#06B6D4] rounded-full transition-all duration-500" style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }} />
                    </div>
                  </div>
                </div>

                <div className="p-4 md:p-6">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentStep}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                      {renderStep()}
                    </motion.div>
                  </AnimatePresence>
                </div>

                <AnimatePresence>
                  {(validationError || saveError) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mx-4 md:mx-6 mb-4"
                    >
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.25)]">
                        <Icon name="AlertCircle" size={16} color="#EF4444" />
                        <p className="text-xs text-[#EF4444]">{validationError || saveError}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-center justify-between px-4 md:px-6 py-4 border-t border-[rgba(6,182,212,0.1)] bg-[rgba(6,182,212,0.02)]">
                  <Button variant="outline" onClick={handleBack} disabled={currentStep === 1} iconName="ChevronLeft" iconPosition="left" size="default">Back</Button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setPreviewOpen((p) => !p)}
                      className="lg:hidden flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[rgba(6,182,212,0.2)] bg-[rgba(6,182,212,0.06)] text-[#06B6D4] text-xs font-medium min-h-[44px]"
                    >
                      <Icon name="BarChart3" size={14} color="#06B6D4" />
                      Preview
                    </button>
                    {currentStep < TOTAL_STEPS ? (
                      <Button variant="default" onClick={handleNext} iconName="ChevronRight" iconPosition="right" size="default">Continue</Button>
                    ) : (
                      <Button variant="default" onClick={handleGenerate} loading={isGenerating} iconName={isGenerating ? undefined : 'Sparkles'} iconPosition="left" size="default">
                        {isGenerating ? 'Saving Plan...' : 'Generate Budget Plan'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="hidden lg:block">
                <QuickActionPanel />
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="hidden lg:block sticky top-24">
                <div className="rounded-xl border border-[rgba(6,182,212,0.15)] bg-[rgba(15,23,42,0.8)] backdrop-blur-sm p-4 md:p-5">
                  <BudgetPreview formData={formData} />
                </div>
                <div className="mt-4 p-4 rounded-xl border border-[rgba(16,185,129,0.2)] bg-[rgba(16,185,129,0.05)]">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Lightbulb" size={16} color="#10B981" />
                    <span className="text-sm font-semibold text-[#10B981]">Financial Tip</span>
                  </div>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">
                    {currentStep === 1 && "The 50/30/20 rule suggests allocating 50% of income to needs, 30% to wants, and 20% to savings and debt repayment."}
                    {currentStep === 2 && "Track every expense category honestly. Small recurring costs like subscriptions often add up to hundreds per month."}
                    {currentStep === 3 && "Having a specific, measurable goal increases your likelihood of achieving it by 42%. Set realistic timelines!"}
                    {currentStep === 4 && "Even a conservative savings rate of 10% can build significant wealth over time through compound interest."}
                  </p>
                </div>
              </div>
              <AnimatePresence>
                {previewOpen && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden overflow-hidden">
                    <div className="rounded-xl border border-[rgba(6,182,212,0.15)] bg-[rgba(15,23,42,0.8)] backdrop-blur-sm p-4 mb-4">
                      <BudgetPreview formData={formData} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </main>
      <AnimatePresence>
        {showSuccess && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] flex items-center justify-center bg-[rgba(2,6,23,0.9)] backdrop-blur-sm">
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 20 }} className="flex flex-col items-center gap-4 p-8 rounded-2xl border border-[rgba(6,182,212,0.3)] bg-[rgba(15,23,42,0.95)] max-w-sm mx-4 text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#06B6D4] to-[#10B981] flex items-center justify-center">
                <Icon name="CheckCircle" size={32} color="#0F172A" strokeWidth={2.5} />
              </div>
              <h2 className="text-xl font-bold text-[#F8FAFC]">Budget Plan Created!</h2>
              <p className="text-sm text-[#94A3B8]">Your personalized financial plan is ready. Redirecting to your dashboard...</p>
              <div className="flex gap-1">
                {[0, 1, 2]?.map((i) => (
                  <motion.div key={i} className="w-2 h-2 rounded-full bg-[#06B6D4]" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }} />
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}